import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { PlanPoint } from "@sts/geometry";
import type { ApiClient } from "../api/client.js";
import { ApiError } from "../api/client.js";
import type { PlanImageView } from "../api/types.js";
import { toView, viewTurnDeg, type PlanFit } from "./plan-view.js";

/**
 * A picture of the floor plan under a storey (migration 0057) — the estate
 * agent's, a sketch — to trace rooms over on a survey with no scan.
 *
 * Its top-left corner sits at (originX, originY) in plan metres and each of its
 * pixels is \`metresPerPx\` across; until somebody sets the scale from a length
 * they know, it is drawn as if 10 m wide and says it is not to scale. It turns
 * with the plan (\`PlanFit.rotationDeg\`) and never takes a click: every tool
 * works through it as if it were paper under tracing film.
 */

export interface PlanImagesSource {
  list(): Promise<readonly PlanImageView[]>;
  upload(storey: number, file: Blob): Promise<PlanImageView>;
  objectUrl(storey: number): Promise<string>;
  update(
    storey: number,
    patch: { readonly metresPerPx?: number; readonly originX?: number; readonly originY?: number; readonly opacity?: number; readonly hidden?: boolean },
  ): Promise<PlanImageView>;
  remove(storey: number): Promise<void>;
}

export function planImagesFromApi(client: ApiClient, surveyId: string): PlanImagesSource {
  return {
    list: () => client.planImages(surveyId),
    upload: (storey, file) => client.uploadPlanImage(surveyId, storey, file),
    objectUrl: (storey) => client.planImageObjectUrl(surveyId, storey),
    update: (storey, patch) => client.updatePlanImage(surveyId, storey, patch),
    remove: (storey) => client.deletePlanImage(surveyId, storey),
  };
}

/** Metres per pixel: the picture's own once set; until then as if 10 m across. */
export function planImageScale(image: PlanImageView): number {
  return image.metresPerPx ?? 10 / Math.max(1, image.widthPx);
}

/** The picture's corners in plan metres, before the plan's turn. */
export function planImageCorners(image: PlanImageView): PlanPoint[] {
  const s = planImageScale(image);
  const w = image.widthPx * s;
  const h = image.heightPx * s;
  const { originX: x, originY: y } = image;
  return [
    { x, y },
    { x: x + w, y },
    { x: x + w, y: y + h },
    { x, y: y + h },
  ];
}

/**
 * Where the picture's corner goes so that the first of two picked points stays
 * put while the scale becomes \`realM\` metres between them.
 */
export function rescaledPlacement(
  image: PlanImageView,
  a: PlanPoint,
  b: PlanPoint,
  realM: number,
): { metresPerPx: number; originX: number; originY: number } {
  const s0 = planImageScale(image);
  const pxApart = Math.hypot(b.x - a.x, b.y - a.y) / s0;
  const s = realM / pxApart;
  const k = s / s0;
  return {
    metresPerPx: s,
    originX: a.x - (a.x - image.originX) * k,
    originY: a.y - (a.y - image.originY) * k,
  };
}

export interface PlanImageState {
  readonly status: "unavailable" | "loading" | "ready" | "failed";
  readonly image: PlanImageView | null;
  readonly url: string | null;
  readonly busy: boolean;
  readonly error: string | null;
  readonly upload: (file: File) => void;
  readonly update: (patch: Parameters<PlanImagesSource["update"]>[1]) => void;
  readonly remove: () => void;
}

export function usePlanImage(source: PlanImagesSource | undefined, storey: number): PlanImageState {
  const [status, setStatus] = useState<PlanImageState["status"]>(source ? "loading" : "unavailable");
  const [images, setImages] = useState<ReadonlyMap<number, PlanImageView>>(new Map());
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const image = images.get(storey) ?? null;
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!source) return;
    let live = true;
    setStatus("loading");
    Promise.resolve()
      .then(() => source.list())
      .then((list) => {
        if (!live) return;
        setImages(new Map(list.map((i) => [i.storey, i])));
        setStatus("ready");
      })
      // An API that predates plan pictures: the plan is drawn without one.
      .catch(() => live && setStatus("failed"));
    return () => {
      live = false;
    };
  }, [source]);

  const sha = image?.sha256 ?? null;
  useEffect(() => {
    if (!source || sha === null) {
      setUrl(null);
      return;
    }
    let live = true;
    let made: string | null = null;
    Promise.resolve()
      .then(() => source.objectUrl(storey))
      .then((u) => {
        if (!live) {
          URL.revokeObjectURL(u);
          return;
        }
        made = u;
        setUrl(u);
      })
      .catch(() => live && setError("The floor-plan picture could not be loaded."));
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [source, storey, sha]);

  const put = (next: PlanImageView | null): void =>
    setImages((all) => {
      const copy = new Map(all);
      if (next) copy.set(storey, next);
      else copy.delete(storey);
      return copy;
    });
  const fail = (e: unknown, words: string): void => setError(e instanceof ApiError ? e.message : words);

  const upload = useCallback(
    (file: File) => {
      if (!source) return;
      setBusy(true);
      setError(null);
      source
        .upload(storey, file)
        .then(put)
        .catch((e: unknown) => fail(e, "The picture could not be uploaded."))
        .finally(() => setBusy(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, storey],
  );

  const update = useCallback(
    (patch: Parameters<PlanImagesSource["update"]>[1]) => {
      if (!source || !image) return;
      // Shown at once; an opacity dragged along a slider is saved when it stops.
      put({ ...image, ...patch });
      if (pending.current) clearTimeout(pending.current);
      pending.current = setTimeout(() => {
        setError(null);
        source
          .update(storey, patch)
          .then(put)
          .catch((e: unknown) => fail(e, "The change could not be saved."));
      }, patch.opacity !== undefined && Object.keys(patch).length === 1 ? 350 : 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, storey, image],
  );

  const remove = useCallback(() => {
    if (!source) return;
    setBusy(true);
    source
      .remove(storey)
      .then(() => put(null))
      .catch((e: unknown) => fail(e, "The picture could not be removed."))
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, storey]);

  return { status, image, url, busy, error, upload, update, remove };
}

/** The picture itself, under everything else on the plan, never taking a click. */
export function PlanImageLayer({
  image,
  url,
  fit,
}: {
  readonly image: PlanImageView;
  readonly url: string;
  readonly fit: PlanFit;
}): ReactNode {
  const o = toView({ x: image.originX, y: image.originY }, fit);
  const scale = fit.pixelsPerMetre * planImageScale(image);
  return (
    <g
      className="floor-plan__picture"
      data-testid="plan-picture"
      transform={`translate(${o.x} ${o.y}) rotate(${viewTurnDeg(fit)}) scale(${scale})`}
      style={{ pointerEvents: "none" }}
    >
      <image href={url} width={image.widthPx} height={image.heightPx} opacity={image.opacity} preserveAspectRatio="none" />
    </g>
  );
}

/** The side panel's "Floor-plan picture" section. */
export function PlanImagePanel({
  state,
  canEdit,
  scaling,
  onStartScale,
  onCancelScale,
  onApplyScale,
}: {
  readonly state: PlanImageState;
  readonly canEdit: boolean;
  /** The points picked so far while setting the scale; null when not setting it. */
  readonly scaling: readonly PlanPoint[] | null;
  readonly onStartScale: () => void;
  readonly onCancelScale: () => void;
  readonly onApplyScale: (realM: number) => void;
}): ReactNode {
  const [length, setLength] = useState("");
  if (state.status === "unavailable" || state.status === "failed") return null;
  const { image } = state;
  if (!image && !canEdit) return null;
  const file = (
    <label className="floor-plan__tool plan-picture__file">
      {image ? "Replace picture" : "Upload a floor-plan picture"}
      <input
        type="file"
        accept="image/png,image/jpeg"
        disabled={state.busy}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) state.upload(f);
          e.target.value = "";
        }}
      />
    </label>
  );
  return (
    <section className="plan-picture" data-testid="plan-picture-panel" aria-label="Floor-plan picture">
      <p className="floor-plan__edit-subtitle">Floor-plan picture</p>
      {!image ? (
        <>
          <p className="muted small">
            An estate agent&apos;s plan or a sketch (PNG or JPG) under this storey, to trace the rooms over.
          </p>
          {file}
        </>
      ) : (
        <>
          {image.metresPerPx === null ? (
            <p className="notice warn small" data-testid="plan-picture-unscaled">
              Not to scale yet. Set its scale from a length you know — a wall with a dimension on the picture.
            </p>
          ) : (
            <p className="muted small">
              To scale: {(1 / image.metresPerPx / 100).toFixed(1)} pixels to the centimetre
              {image.updatedBy ? ` · set by ${image.updatedBy.name}` : ""}.
            </p>
          )}
          {canEdit ? (
            <>
              {scaling === null ? (
                <button type="button" className="floor-plan__tool" onClick={onStartScale}>
                  Set its scale
                </button>
              ) : scaling.length < 2 ? (
                <p className="small" role="status">
                  Click {scaling.length === 0 ? "the start" : "the end"} of a length you know on the picture.{" "}
                  <button type="button" className="floor-plan__link" onClick={onCancelScale}>
                    Cancel
                  </button>
                </p>
              ) : (
                <form
                  className="plan-picture__length"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const m = Number(length.replace(",", "."));
                    if (m > 0) {
                      onApplyScale(m);
                      setLength("");
                    }
                  }}
                >
                  <label>
                    <span className="small">How far apart are they, really?</span>
                    <span className="site-survey__number">
                      <input aria-label="Real length between the two points, metres" inputMode="decimal" value={length} autoFocus onChange={(e) => setLength(e.target.value)} />
                      <span className="muted">m</span>
                    </span>
                  </label>
                  <button type="submit" className="floor-plan__tool floor-plan__tool--primary" disabled={!(Number(length.replace(",", ".")) > 0)}>
                    Set scale
                  </button>
                  <button type="button" className="floor-plan__link" onClick={onCancelScale}>
                    Cancel
                  </button>
                </form>
              )}
              <label className="plan-picture__opacity small">
                Show it
                <input
                  type="range"
                  min={0.1}
                  max={1}
                  step={0.05}
                  value={image.opacity}
                  aria-label="How strongly the picture shows"
                  onChange={(e) => state.update({ opacity: Number(e.target.value) })}
                />
              </label>
              <div className="plan-picture__actions">
                <button type="button" className="floor-plan__link" onClick={() => state.update({ hidden: !image.hidden })}>
                  {image.hidden ? "Show the picture" : "Hide the picture"}
                </button>
                {file}
                <button type="button" className="floor-plan__link" disabled={state.busy} onClick={state.remove}>
                  Remove
                </button>
              </div>
            </>
          ) : null}
        </>
      )}
      {state.error ? (
        <p className="floor-plan__panel-error" role="alert">
          {state.error}
        </p>
      ) : null}
    </section>
  );
}
